<template>
  <b-sidebar
    id="edit-product-sidebar"
    :visible="productEditSidebarActive"
    bg-variant="white"
    sidebar-class="sidebar-lg"
    shadow
    backdrop
    no-header
    right
    @change="(visible) => $emit('update:product-edit-sidebar-active', visible)"
  >
    <template #default="{ hide }">
      <!-- Header -->
      <div class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1">
        <h5 class="mb-0">
          Editar Producto
        </h5>
        <feather-icon
          class="ml-1 cursor-pointer"
          icon="XIcon"
          size="16"
          @click="hide"
        />
      </div>

      <!-- Loading -->
      <div
        v-if="loading"
        class="text-center py-5"
      >
        <b-spinner variant="primary" />
        <p class="mt-2">
          Cargando producto...
        </p>
      </div>

      <!-- Formulario de edición -->
      <validation-observer
        v-else
        ref="productForm"
        v-slot="{ handleSubmit }"
      >
        <b-form
          class="p-2"
          @submit.prevent="handleSubmit(onSubmit)"
        >
          <!-- Información General -->
          <h4 class="mb-2">
            <feather-icon
              icon="PackageIcon"
              size="18"
              class="mr-1"
            />
            Información General
          </h4>

          <b-row>
            <b-col cols="12">
              <validation-provider
                v-slot="{ errors }"
                name="Código"
                rules="required|max:50"
              >
                <b-form-group
                  label="Código"
                  label-for="edit-code"
                >
                  <b-form-input
                    id="edit-code"
                    v-model="editData.code"
                    :state="errors.length === 0"
                    placeholder="Ej: 100750"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>

              <!-- SKU -->
              <validation-provider
                v-slot="{ errors }"
                name="SKU"
                rules="max:100"
              >
                <b-form-group
                  label="SKU"
                  label-for="edit-sku"
                >
                  <b-form-input
                    id="edit-sku"
                    v-model="editData.sku"
                    :state="errors.length === 0"
                    placeholder="Ej: SKU-100750-BLUE"
                  />
                  <small class="text-muted">Código único del producto + color (opcional)</small>
                  <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>

              <validation-provider
                v-slot="{ errors }"
                name="Descripción"
                rules="required|max:200"
              >
                <b-form-group
                  label="Descripción"
                  label-for="edit-description"
                >
                  <b-form-textarea
                    id="edit-description"
                    v-model="editData.description"
                    :state="errors.length === 0"
                    placeholder="Descripción del producto"
                    rows="3"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>

              <!-- Base Price -->
              <validation-provider
                v-slot="{ errors }"
                name="Precio Base"
                rules="decimal:2"
              >
                <b-form-group
                  label="Precio Base"
                  label-for="edit-base_price"
                >
                  <b-form-input
                    id="edit-base_price"
                    v-model="editData.base_price"
                    type="number"
                    step="0.01"
                    min="0"
                    :state="errors.length === 0"
                    placeholder="0.00"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>

              <validation-provider
                v-slot="{ errors }"
                name="Estado"
                rules="required"
              >
                <b-form-group
                  label="Estado"
                  label-for="edit-status"
                >
                  <v-select
                    id="edit-status"
                    v-model="editData.is_active"
                    :options="statusOptions"
                    :reduce="status => status.value"
                    :state="errors.length === 0"
                    placeholder="Seleccionar estado"
                    label="label"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>
            </b-col>
          </b-row>

          <!-- Propiedades -->
          <h4 class="mb-2 mt-2">
            <feather-icon
              icon="GridIcon"
              size="18"
              class="mr-1"
            />
            Propiedades
          </h4>

          <b-row>
            <b-col cols="12">
              <validation-provider
                v-slot="{ errors }"
                name="Color"
                rules="required"
              >
                <b-form-group
                  label="Color"
                  label-for="edit-color"
                >
                  <v-select
                    id="edit-color"
                    v-model="editData.color_id"
                    :options="colorOptions"
                    :reduce="color => color.id"
                    :state="errors.length === 0"
                    placeholder="Seleccionar color"
                    label="name"
                  >
                    <template #option="{ name, hex_code }">
                      <div class="d-flex align-items-center">
                        <div
                          v-if="hex_code"
                          class="color-preview mr-2"
                          :style="{ backgroundColor: hex_code }"
                        />
                        <span>{{ name }}</span>
                      </div>
                    </template>
                  </v-select>
                  <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>

              <validation-provider
                v-slot="{ errors }"
                name="Categoría"
                rules="required"
              >
                <b-form-group
                  label="Categoría"
                  label-for="edit-category"
                >
                  <v-select
                    id="edit-category"
                    v-model="editData.category_id"
                    :options="categoryOptions"
                    :reduce="category => category.id"
                    :state="errors.length === 0"
                    placeholder="Seleccionar categoría"
                    label="name"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>

              <!-- Width -->
              <validation-provider
                v-slot="{ errors }"
                name="Ancho"
                rules="max:50"
              >
                <b-form-group
                  label="Ancho"
                  label-for="edit-width"
                >
                  <b-form-input
                    id="edit-width"
                    v-model="editData.width"
                    :state="errors.length === 0"
                    placeholder="Ej: 1.5m, 100cm"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>

              <!-- Web Name -->
              <validation-provider
                v-slot="{ errors }"
                name="Nombre Web"
                rules="max:200"
              >
                <b-form-group
                  label="Nombre Web"
                  label-for="edit-web_name"
                >
                  <b-form-input
                    id="edit-web_name"
                    v-model="editData.web_name"
                    :state="errors.length === 0"
                    placeholder="Nombre para mostrar en la web"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>

              <!-- Web Description -->
              <validation-provider
                v-slot="{ errors }"
                name="Descripción Web"
              >
                <b-form-group
                  label="Descripción Web"
                  label-for="edit-web_description"
                >
                  <b-form-textarea
                    id="edit-web_description"
                    v-model="editData.web_description"
                    :state="errors.length === 0"
                    placeholder="Descripción detallada para la web"
                    rows="4"
                  />
                  <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>

              <!-- Bullet Points -->
              <validation-provider
                v-slot="{ errors }"
                name="Puntos Clave"
              >
                <b-form-group
                  label="Puntos Clave"
                  label-for="edit-bullet_points"
                >
                  <b-form-textarea
                    id="edit-bullet_points"
                    v-model="editData.bullet_points"
                    :state="errors.length === 0"
                    placeholder="Características principales del producto (una por línea)"
                    rows="4"
                  />
                  <small class="text-muted">Ingresa una característica por línea</small>
                  <small class="text-danger">{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>
            </b-col>
          </b-row>

          <!-- Botones de acción -->
          <div class="d-flex mt-3">
            <b-button
              variant="primary"
              type="submit"
              block
              :disabled="isSubmitting"
            >
              <b-spinner
                v-if="isSubmitting"
                small
                class="mr-1"
              />
              {{ isSubmitting ? 'Guardando...' : 'Guardar Cambios' }}
            </b-button>
          </div>
        </b-form>
      </validation-observer>
    </template>
  </b-sidebar>
</template>

<script>
import axios from '@axios'
import vSelect from 'vue-select'
import { ValidationProvider, ValidationObserver } from 'vee-validate'

export default {
  components: {
    vSelect,
    ValidationProvider,
    ValidationObserver,
  },

  props: {
    productEditSidebarActive: {
      type: Boolean,
      required: true,
    },
    productId: {
      type: Number,
      required: true,
    },
  },

  data() {
    return {
      loading: true,
      isSubmitting: false,
      productData: null,
      editData: {
        code: '',
        sku: '',
        description: '',
        base_price: null,
        width: '',
        web_name: '',
        web_description: '',
        bullet_points: '',
        color_id: null,
        category_id: null,
        is_active: 'Y',
      },
      colorOptions: [],
      categoryOptions: [],
      statusOptions: [
        { label: 'Activo', value: 'Y' },
        { label: 'Inactivo', value: 'N' },
      ],
    }
  },

  watch: {
    productEditSidebarActive(val) {
      if (val && this.productId) {
        this.loadProduct()
        this.loadOptions()
      }
    },
  },

  methods: {
    async loadProduct() {
      this.loading = true
      try {
        const response = await axios.get(`product/${this.productId}`)
        this.productData = response.data.data

        // Inicializar datos de edición
        this.editData = {
          code: this.productData.code,
          sku: this.productData.sku,
          description: this.productData.description,
          base_price: this.productData.base_price,
          width: this.productData.width,
          web_name: this.productData.web_name,
          web_description: this.productData.web_description,
          bullet_points: this.productData.bullet_points,
          color_id: this.productData.color_id,
          category_id: this.productData.category_id,
          is_active: this.productData.is_active,
        }
      } catch (error) {
        this.showErrors(error)
      } finally {
        this.loading = false
      }
    },

    async loadOptions() {
      try {
        // Cargar colores
        const colorsResponse = await axios.get('color?perPage=100&filterField=status&filterValue=Y')
        this.colorOptions = colorsResponse.data.data

        // Cargar categorías
        const categoriesResponse = await axios.get('category?perPage=100&filterField=status&filterValue=Y')
        this.categoryOptions = categoriesResponse.data.data
      } catch (error) {
        this.showErrors(error)
      }
    },

    async onSubmit() {
      this.isSubmitting = true

      try {
        await axios.put(`product/${this.productId}`, this.editData)

        this.$bvToast.toast('Producto actualizado exitosamente', {
          title: 'Éxito',
          variant: 'success',
          solid: true,
        })

        // Emitir evento para actualizar la vista
        this.$emit('refreshData')
        this.$emit('update:product-edit-sidebar-active', false)
      } catch (error) {
        this.showErrors(error)
      } finally {
        this.isSubmitting = false
      }
    },

    showErrors(error) {
      if (error.response && error.response.data && error.response.data.message) {
        this.$bvToast.toast(error.response.data.message, {
          title: 'Error',
          variant: 'danger',
          solid: true,
        })
      } else {
        this.$bvToast.toast('Ha ocurrido un error inesperado', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        })
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.color-preview {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 1px solid #ddd;
}

.content-sidebar-header {
  border-bottom: 1px solid #d8d6de;
}
</style>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
